// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getCookie } from "@insite/client-framework/Common/Cookies";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import logger from "@insite/client-framework/Logger";
import { getExternalProviders } from "@insite/client-framework/Services/IdentityService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentUserIsGuest, getIsPunchOutSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import loadExternalProviders from "@insite/client-framework/Store/Pages/SignIn/Handlers/LoadExternalProviders";
import setIsReorderConfirmationModalOpen from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetIsReorderConfirmationModalOpen";
import setNextAction from "@insite/client-framework/Store/Pages/VmiBins/Handlers/SetNextAction";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem from "@insite/mobius/GridItem";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import { Hidden } from "@insite/mobius/index";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import getColor from "@insite/mobius/utilities/getColor";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import setShowDropShadow from "@rc/App/Handlers/XNG/showDropShadow";
import Tooltip from "@rc/MobiusOverrides/Tooltip/Tooltip";
import AccountInactive from "@rc/XDL/Icons/AccountInactive";
import { signInExternalProvidersHandler } from "@rc/XDL/Utilities/signInExternalProvidersHandler";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    links = "links",
    showRcAcademyLinkForReadOnly = "showRcAcademyLinkForReadOnly",
    rcAcademySupportedCustomerTypes = "rcAcademySupportedCustomerTypes",
    rcAcademySupportedCustomerNumbers = "rcAcademySupportedCustomerNumbers",
    rcAcademyLinkTitle = "rcAcademyLinkTitle",
    showBreederRecommendationPortalLink = "showBreederRecommendationPortalLink",
    showMakeAPayment = "showMakeAPayment",
    makeAPaymentLinkTitle = "makeAPaymentLinkTitle",
}

const mapStateToProps = (state: ApplicationState) => {
    const billToState = getBillToState(state, state.context.session.billToId);
    const shipToState = getShipToState(state, state.context.session.shipToId);
    const cart = getCurrentCartState(state).value;
    const customerType = cart?.properties?.["customerType"]?.toLowerCase();
    const session = getSession(state);

    const primarySalesperson =
        state.context.session?.properties?.primarySalesperson &&
        JSON.parse(state.context.session?.properties?.primarySalesperson);

    let billTo;
    if (billToState && billToState.value) {
        billTo = billToState.value;
    }

    let shipTo;
    if (shipToState && shipToState.value) {
        shipTo = shipToState.value;
    }
    return {
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        accountDashboardPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        changeCustomerPageLink: getPageLinkByPageType(state, "ChangeCustomerPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        isReorderConfirmationModalOpen: state.pages.vmiBins.isReorderConfirmationModalOpen,
        hasChangeLastQty:
            state.pages.vmiBins.selectVmiBinsChangedQty &&
            Object.keys(state.pages.vmiBins.selectVmiBinsChangedQty).length > 0,
        billTo,
        shipTo,
        primarySalesperson,
        navDrawerIsOpen: state.components.addressDrawer.navDrawerIsOpen,
        customerType,
        isReadOnly: session?.userRoles?.indexOf("ReadOnly") !== -1,
        externalProviders: state.pages.signIn.externalProviders,
        location: getLocation(state),
    };
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setShowInventory,
    setShowPricing,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    setShowDropShadow,
    loadExternalProviders,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    myAccountOverlayTitle?: InjectableCss;
    accountDashboardTitle?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    accountOverlayLink?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    signOutTypography?: TypographyPresentationProps;
    changeCustomerTypography?: TypographyPresentationProps;
    customerInfoTypography?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
    salesRepPhoneLink?: LinkPresentationProps;
    salesRepEmailLink?: LinkPresentationProps;
    mobileElementIcon?: IconPresentationProps;
    Link: LinkPresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            display: inline-flex;
            padding: 6px 0;
        `,
    },
    myAccountOverlayTitle: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui3")};
            padding-bottom: 5px;
            color: ${getColor("common.ui6")};
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;
            display: block;
            width: 100%;
            margin-bottom: 15px;
        `,
    },
    accountDashboardTitle: {
        css: css`
            border-bottom: 1px solid ${getColor("common.ui3")};
            padding-bottom: 5px;
            color: ${getColor("common.ui6")};
            font-size: 16px;
            font-weight: 500;
            line-height: 24px;
            display: block;
            width: 100%;
            &:hover {
                color: ${getColor("primary.main")};
            }
        `,
    },
    salesRepPhoneLink: {
        color: "primary",
        typographyProps: {
            size: 12,
            css: css`
                line-height: 13px;
            `,
        },
        icon: {
            iconProps: {
                src: "PhoneHover",
                size: 12,
                color: "common.ui10",
                css: css`
                    margin-right: 8px;
                `,
            },
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
    salesRepEmailLink: {
        color: "primary",
        typographyProps: {
            size: 12,
            css: css`
                line-height: 13px;
            `,
        },
        icon: {
            iconProps: {
                src: "MailSmall",
                size: 12,
                color: "common.ui10",
                css: css`
                    margin-right: 8px;
                `,
            },
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
    customerInfoTypography: {
        css: css`
            line-height: 13px;
            margin-bottom: 8px;
        `,
    },
    signOutTypography: {
        css: css`
            color: ${getColor("common.ui1")};
            font-size: 16px;
            font-weight: 500;
            line-height: 17px;
            text-align: center;
            width: 100%;
        `,
    },
    changeCustomerTypography: {
        css: css`
            color: ${getColor("common.ui1")};
            font-size: 16px;
            font-weight: 500;
            line-height: 17px;
            text-align: center;
            width: 100%;
        `,
    },
    mobileElementIcon: {
        size: 20,
        src: "AccountInactive",
        color: "common.ui4",
    },
    titleClickable: {
        css: css`
            margin-left: 10px;
            align-items: end;
            &:hover,
            &:focus {
                ${TypographyStyle} {
                    color: ${getColor("primary.main")};
                }
            }
        `,
    },
    titleIcon: {
        size: 18,
        color: "common.ui4",
    },
    titleTypography: {
        lineHeight: "20px",
        size: 16,
        weight: 500,
        color: "text",
        css: css`
            margin-left: 8px;
        `,
        ellipsis: true,
        transform: "capitalize",
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
        `,
    },
    accountOverlayLink: {
        css: css`
            &,
            span {
                font-weight: 500;
                line-height: 13px;
                color: ${getColor("common.ui10")};
            }

            &:hover,
            &:focus {
                color: ${getColor("primary")};
            }
        `,
    },
    Link: {
        css: css`
            &:hover {
                text-decoration: none;
            }
            span {
                font-size: 12px;
                font-weight: 500;
                line-height: 13px;
            }
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.links]: LinkModel[];
        [fields.showRcAcademyLinkForReadOnly]: boolean;
        [fields.rcAcademySupportedCustomerTypes]: string;
        [fields.rcAcademySupportedCustomerNumbers]: string;
        [fields.rcAcademyLinkTitle]: string;
        [fields.makeAPaymentLinkTitle]: string;
        [fields.showBreederRecommendationPortalLink]: boolean;
        [fields.showMakeAPayment]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & HasHistory;

let icon: React.ComponentType<IconPresentationProps>;
export const HeaderSignIn = ({
    userName,
    myAccountPageLink,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    accountDashboardPageLink,
    changeCustomerPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    billTo,
    shipTo,
    primarySalesperson,
    setShowDropShadow,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    customerType,
    setShowInventory,
    setShowPricing,
    isReorderConfirmationModalOpen,
    hasChangeLastQty,
    setIsReorderConfirmationModalOpen,
    setNextAction,
    isReadOnly,
    externalProviders,
    loadExternalProviders,
    location,
}: Props) => {
    icon = AccountInactive;

    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    const [myAccountTippy, setTippyInstance] = React.useState<any>();
    const [showRcAcademyLinkForReadOnly, setShowRcAcademyLinkForReadOnly] = React.useState(false);
    const rcAcademySupportedCustomerTypes = fields?.rcAcademySupportedCustomerTypes;
    const rcAcademySupportedCustomerNumbers = fields?.rcAcademySupportedCustomerNumbers;
    const rcAcademyLinkTitle = fields?.rcAcademyLinkTitle;
    const makeAPaymentLinkTitle = fields?.makeAPaymentLinkTitle;

    useEffect(() => {
        if (!externalProviders) {
            loadExternalProviders();
        }
    }, [externalProviders]);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    React.useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    React.useEffect(() => {
        if (
            rcAcademySupportedCustomerTypes?.toLowerCase().includes(customerType as any) ||
            rcAcademySupportedCustomerNumbers?.toLowerCase().includes(billTo?.customerNumber?.toLowerCase() as any) ||
            (fields?.showRcAcademyLinkForReadOnly && isReadOnly)
        ) {
            setShowRcAcademyLinkForReadOnly(true);
        }
    }, [rcAcademySupportedCustomerTypes, rcAcademySupportedCustomerNumbers, billTo, customerType]);

    if (!myAccountPageLink) {
        return null;
    }

    const hideTippy = () => {
        setTimeout(() => {
            hideAll();
        }, 500);
    };

    const { showInventoryToggle, showPricingToggle } = fields;
    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }

    const onSignInHandler = (e: React.MouseEvent) => {
        hideTippy();
        // https://xngage.atlassian.net/browse/RC-2277
        signInExternalProvidersHandler(e, undefined, userName, currentUserIsGuest, externalProviders, signInUrl, location);
    };

    const onSignOutHandler = () => {
        if (!isReorderConfirmationModalOpen && hasChangeLastQty) {
            setIsReorderConfirmationModalOpen({ isReorderConfirmationModalOpen: true });
            setNextAction({ nextAction: signOutFunc });
        } else {
            signOutFunc();
        }
    };

    const signOutFunc = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
        myAccountTippy && myAccountTippy.hide();
    };

    let signInStatusText = translate("Sign In");
    if (userName && !currentUserIsGuest) {
        signInStatusText = userName;
    }

    const mobileElementClickHandler = (e: any) => {
        if (userName && !currentUserIsGuest) {
            history.push("/MyAccount/");
        } else {
            onSignInHandler(e);
        }
    };

    const UserElement = (
        <Clickable
            {...styles.titleClickable}
            onClick={() => history.push(accountDashboardPageLink?.url as string)}
            data-test-selector="header_signIn"
        >
            <Icon {...styles.titleIcon} src={icon} />
            <Typography {...styles.titleTypography} data-test-selector="header_userName">
                {translate("My Account")}
            </Typography>
        </Clickable>
    );
    const MobileElement = () => (
        <StyledWrapper className="mobile-element">
            <Clickable
                data-test-selector="header_signIn"
                aria-label="sign in"
                className="mobile-title-clickable"
                onClick={() => {
                    hideTippy();
                    // eslint-disable-next-line no-restricted-globals
                    mobileElementClickHandler(event);
                }}
            >
                <Icon {...styles.mobileElementIcon} />
            </Clickable>
        </StyledWrapper>
    );
    const MyAccountOverlay = () => (
        <StyledWrapper
            css={css`
                background-color: ${getColor("common.ui1")};
                padding: 0;
            `}
        >
            <StyledWrapper
                css={css`
                    padding: 17px 17px 0;
                `}
            >
                <StyledWrapper
                    css={css`
                        margin-bottom: 23px;
                        display: flex;
                        flex-wrap: wrap;
                    `}
                >
                    <GridContainer gap={15}>
                        {fields.links && fields.links.length > 0 && (
                            <GridItem width={12}>
                                <Clickable
                                    href={accountDashboardPageLink?.url || ""}
                                    {...styles.accountDashboardTitle}
                                    onClick={hideTippy}
                                >
                                    {translate("Account Dashboard")}
                                </Clickable>
                            </GridItem>
                        )}

                        {fields.links?.map((fieldLink, index) => {
                            const { overriddenTitle, destination, openInNewTab } = (fieldLink as any).fields;
                            const linkValue = useGetLink(destination);
                            if (!linkValue?.url || (linkValue as any).excludeFromNavigation) {
                                return null;
                            }
                            return (
                                <GridItem
                                    width={[12, 6, 6, 6, 6]}
                                    css={css`
                                        margin-bottom: 10px;
                                    `}
                                    key={`${overriddenTitle}_${destination.value}${index}`}
                                >
                                    <Clickable
                                        href={linkValue.url}
                                        onClick={hideTippy}
                                        target={openInNewTab ? "_blank" : "_self"}
                                        {...styles.accountOverlayLink}
                                    >
                                        {overriddenTitle || linkValue?.title}
                                    </Clickable>
                                </GridItem>
                            );
                        })}
                        {showRcAcademyLinkForReadOnly && (
                            <GridItem
                                width={[12, 6, 6, 6, 6]}
                                css={css`
                                    margin-bottom: 10px;
                                `}
                            >
                                {createWidgetElement("RcAcademy/RcAcademy", {
                                    fields: {
                                        rcAcademyLinkTitle,
                                        customCSS: `
                                                button:hover {
                                                    text-decoration: none;
                                                }
                                                span {
                                                    font-size: 12px;
                                                    font-weight: 500;
                                                    line-height: 13px;
                                                }
                                            `,
                                    },
                                })}
                            </GridItem>
                        )}
                        {fields.showBreederRecommendationPortalLink && (
                            <GridItem
                                width={[12, 6, 6, 6, 6]}
                                css={css`
                                    margin-bottom: 10px;
                                `}
                            >
                                {createWidgetElement("BreederRecommendationPortal/BreederRecommendationPortal", {
                                    fields: {
                                        customCSS: `
                                                button:hover {
                                                    text-decoration: none;
                                                }
                                                span {
                                                    font-size: 12px;
                                                    font-weight: 500;
                                                    line-height: 13px;
                                                }
                                            `,
                                    },
                                })}
                            </GridItem>
                        )}
                        {fields.showMakeAPayment && !isReadOnly && (
                            <GridItem
                                width={[12, 6, 6, 6, 6]}
                                css={css`
                                    margin-bottom: 10px;
                                `}
                            >
                                {createWidgetElement("MakeAPayment/MakeAPayment", {
                                    fields: {
                                        makeAPaymentLinkTitle,
                                        customCSS: `
                                            button:hover {
                                                text-decoration: none;
                                            }
                                            span {
                                                font-size: 12px;
                                                font-weight: 500;
                                                line-height: 13px;
                                            }
                                        `,
                                    },
                                })}
                            </GridItem>
                        )}
                    </GridContainer>
                </StyledWrapper>

                <GridContainer gap={22}>
                    <GridItem
                        width={6}
                        css={css`
                            display: flex;
                            flex-direction: column;
                        `}
                    >
                        <Typography {...styles.myAccountOverlayTitle}>{translate("Customer Information")}</Typography>

                        {billTo && billTo.companyName && (
                            <Typography {...styles.customerInfoTypography}>{billTo?.companyName}</Typography>
                        )}
                        {billTo && billTo.customerNumber && (
                            <Typography {...styles.customerInfoTypography}>{billTo?.customerNumber}</Typography>
                        )}
                        {shipTo && shipTo.customerSequence && (
                            <Typography {...styles.customerInfoTypography}>{shipTo?.customerSequence}</Typography>
                        )}
                    </GridItem>
                    <GridItem
                        width={6}
                        css={css`
                            display: flex;
                            flex-direction: column;
                        `}
                    >
                        {(primarySalesperson?.email?.length > 0 || primarySalesperson?.phone?.length > 0) && (
                            <Typography {...styles.myAccountOverlayTitle}>
                                {translate("Sales Representative")}
                            </Typography>
                        )}
                        {primarySalesperson?.email?.length > 0 && (
                            <Link href={`mailto:${primarySalesperson.email}`} {...styles.salesRepEmailLink}>
                                {primarySalesperson.email}
                            </Link>
                        )}
                        {primarySalesperson?.phone?.length > 0 && (
                            <Link href={`tel:${primarySalesperson.phone}`} {...styles.salesRepPhoneLink}>
                                {primarySalesperson.phone}
                            </Link>
                        )}
                    </GridItem>
                </GridContainer>
            </StyledWrapper>
            <GridContainer
                gap={0}
                css={css`
                    margin-top: 15px;
                `}
            >
                <GridItem width={6}>
                    <Clickable
                        data-test-selector="header_changecustomer"
                        aria-label="Change Customer"
                        onClick={() => {
                            hideTippy();
                            setShowDropShadow({ showDropShadow: false });
                            history.push(changeCustomerPageLink?.url as string);
                        }}
                        disabled={!displayChangeCustomerLink}
                        css={css`
                            background-color: ${getColor("primary")};
                            width: 100%;
                            padding: 10px 0;
                            &:disabled {
                                opacity: 0.33;
                            }
                        `}
                    >
                        <Typography {...styles.changeCustomerTypography}>{translate("Change Customer")}</Typography>
                    </Clickable>
                </GridItem>
                <GridItem width={6}>
                    <Clickable
                        data-test-selector="header_signout"
                        aria-label="sign out"
                        onClick={onSignOutHandler}
                        css={css`
                            background-color: ${getColor("common.ui5")};
                            width: 100%;
                            padding: 10px 0;
                        `}
                    >
                        <Typography {...styles.signOutTypography}>{translate("Sign Out")}</Typography>
                    </Clickable>
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );

    return (
        <StyledWrapper>
            <Hidden below="lg">
                {userName && !currentUserIsGuest ? (
                    <StyledWrapper>
                        {/* My Account */}
                        <Tooltip
                            trigger="mouseenter focus"
                            onOpen={({ instance }: any) => {
                                hideAll();
                                setTimeout(() => setShowDropShadow({ showDropShadow: true }));
                                setTippyInstance(instance);
                            }}
                            onClose={({ instance }: any) => {
                                setShowDropShadow({ showDropShadow: false });
                                setTippyInstance(instance);
                            }}
                            placement="bottom-end"
                            triggerAltText={translate("My Account Menu")}
                            triggerComponent={UserElement}
                            delay={[100, 350]}
                            offset={[0, 8]}
                            cssOverrides={{
                                tooltipBody: css``,
                                tooltipContainer: css`
                                    &&&& {
                                        max-width: initial;
                                        width: 430px;
                                        padding: 0;
                                        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
                                        border-radius: 0;
                                        border: 1px solid ${getColor("common.ui3")};
                                        overflow: hidden;
                                        margin-top: 11px;
                                        .tippy-arrow {
                                            top: 5px;
                                            left: 12px !important;
                                        }
                                    }
                                `,
                            }}
                            passAsChildren={true}
                        >
                            <MyAccountOverlay />
                        </Tooltip>
                    </StyledWrapper>
                ) : (
                    <Clickable
                        {...styles.titleClickable}
                        onClick={onSignInHandler}
                        href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                        data-test-selector="header_signIn"
                    >
                        <Icon {...styles.titleIcon} src={icon} />
                        <Typography {...styles.titleTypography}>{signInStatusText}</Typography>
                    </Clickable>
                )}
            </Hidden>
            <Hidden above="md">
                <MobileElement />
            </Hidden>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(HeaderSignIn)),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overriddenTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overriddenTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overriddenTitle) {
                            return overriddenTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: false,
                    },
                    {
                        name: "openInNewTab",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
            {
                name: fields.showRcAcademyLinkForReadOnly,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.rcAcademySupportedCustomerTypes,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.rcAcademySupportedCustomerNumbers,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.rcAcademyLinkTitle,
                editorTemplate: "TextField",
                defaultValue: "RC Academy",
                fieldType: "General",
            },
            {
                name: fields.showBreederRecommendationPortalLink,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showMakeAPayment,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.makeAPaymentLinkTitle,
                editorTemplate: "TextField",
                defaultValue: "Make a Payment",
                fieldType: "General",
            },
            // Not used in RC
            // {
            //     name: fields.showPricingToggle,
            //     displayName: "Show/Hide Pricing Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 4,
            // },
            // {
            //     name: fields.showInventoryToggle,
            //     displayName: "Show/Hide Inventory Toggle",
            //     editorTemplate: "CheckboxField",
            //     isVisible: widget => widget.fields.includeAccountMenu,
            //     defaultValue: false,
            //     fieldType: "General",
            //     sortOrder: 5,
            // },
        ],
        group: "Common",
    },
};

export default widgetModule;
